import { faTag, faVideoSlash, faVolumeHigh } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import dayjs from "dayjs";
import { SetStateAction, useEffect, useState } from "react";
import { ListGroup, Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { MediaSource } from "../../../models/media/MediaSource";
import { Task, getTaskProgress } from "../../../models/tasks/Task";
import { ApplicationState } from "../../../store";
import { useGetStoredFileForMediaQuery } from "../../../store/api/kinesense";
import { DateFormats } from "../../../utilities/dates";
import ThumbnailExtractor from "../../../utilities/ThumbnailExtractor";
import { getReviewVideoListItemId } from "./ReviewVideoList";
import ReviewVideoListItemFooter from "./tray/ReviewVideoListItemFooter";
import useTagEventsCount from "./useTagEventsCount";

export interface ReviewVideoListItemProps {
    data: MediaSource;
    viewId: string;
    activeMediaId: string;
    setActiveMediaId: (_: SetStateAction<string>) => void;
    thumbnail: string | undefined;
    hasLoadedThumbnails: boolean;
    task: Task | undefined;
}

function ReviewVideoListItem(props: ReviewVideoListItemProps) {
    const projectId = useSelector((state: ApplicationState) => state.general.activeProjectId);

    const isSelected = props.data.mediaId === props.activeMediaId;
    const hasAudio = props.data.files?.initialDisplay?.hasAudio ?? false;
    const duration = dayjs
        ?.duration(Math.round(props.data.duration))
        .format(DateFormats.durationTimeDaysToMilliseconds)
        .split("#");

    function handleOnSelect(e: React.MouseEvent) {
        e.preventDefault();
        props.setActiveMediaId(props.data.mediaId);
    }

    const matchingEventsCount = useTagEventsCount(projectId, props.data.mediaId) ?? 0;

    const progress = getTaskProgress(props.task);

    // BACKUP THUMBNAIL
    const [isLoadingBackupThumbnail, setIsLoadingBackupThumbnail] = useState(false);
    const [backupThumbnailSrc, setBackupThumbnailSrc] = useState<string>(undefined);

    const {
        data: storedFile,
        isSuccess: isSuccessStoredFile,
        isFetching: isFetchingStoredFile,
        isError: isErrorStoredFile,
    } = useGetStoredFileForMediaQuery(
        {
            projectId: props.data?.projectId,
            mediaId: props.data?.mediaId,
            fileId: props.data?.files.initialDisplay?.fileId,
        },
        {
            skip:
                !props.hasLoadedThumbnails ||
                props.thumbnail !== undefined ||
                props.data === undefined ||
                props.data?.files.initialDisplay?.fileId === undefined,
        },
    );
    const hasLoadedStoredFile = isSuccessStoredFile && !isFetchingStoredFile;

    useEffect(() => {
        if (!hasLoadedStoredFile) {
            if (isErrorStoredFile) {
                setIsLoadingBackupThumbnail(false);
            }
            return;
        }

        setIsLoadingBackupThumbnail(true);
        ThumbnailExtractor.getThumbnail(storedFile.accessUrl.url, [0, 0, 1, 1], 0)
            .then((r) => {
                setBackupThumbnailSrc(r);
            })
            .catch((e) => {
                console.error(e);
            })
            .finally(() => {
                setIsLoadingBackupThumbnail(false);
            });
    }, [hasLoadedStoredFile, isErrorStoredFile]);

    function renderImage() {
        const containerStyle = { width: "90px", height: "70px" };
        const containerClass = "flex-shrink-0 d-flex justify-content-center align-items-center";

        if (!props.hasLoadedThumbnails || (props.thumbnail === undefined && isLoadingBackupThumbnail)) {
            return (
                <div className={containerClass} style={containerStyle}>
                    <Spinner size="sm" />
                </div>
            );
        } else if (props.thumbnail === undefined && backupThumbnailSrc === undefined) {
            return (
                <div
                    className={`video-list-item-thumbnail ${containerClass} bg-secondary-subtle`}
                    style={containerStyle}
                    title="No preview"
                >
                    <FontAwesomeIcon icon={faVideoSlash} className="text-secondary-emphasis" />
                </div>
            );
        } else {
            const thumbnailSrc = props.thumbnail ?? backupThumbnailSrc;
            return (
                <div className={containerClass} style={containerStyle}>
                    <img
                        alt="thumbnail"
                        className="video-list-item-thumbnail"
                        src={thumbnailSrc}
                        style={{ maxWidth: "100%", maxHeight: "100%" }}
                    />
                </div>
            );
        }
    }

    return (
        <ListGroup.Item
            id={getReviewVideoListItemId(props.data.mediaId)}
            as={"div"}
            action
            onClick={handleOnSelect}
            className={`video-list-item shadow-sm${isSelected ? " selected" : ""}`}
        >
            <section className="video-list-item-content">
                {renderImage()}

                <div className="video-list-item-details">
                    <span className="text-truncate flex-fill text-nowrap video-list-item-name">{props.data.name}</span>

                    <div className="video-list-times">
                        <div className="gap-2 d-flex">
                            <span className="text-secondary">Start Time</span>
                            <span className="text-muted flex-fill text-nowrap">
                                {dayjs(props.data.startsAt).format(DateFormats.dayMonthYearWithTimeSeconds)}
                            </span>
                        </div>
                        <div className="gap-3 d-flex">
                            <span className="text-secondary">Duration</span>
                            <div>
                                <span className="text-muted">{duration[0]}</span>
                                <span className="text-secondary seconds-fraction">.{duration[1]}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="video-list-item-icons">
                    <div>{hasAudio && <FontAwesomeIcon className="text-secondary" icon={faVolumeHigh} />}</div>
                    {matchingEventsCount > 0 && !isSelected && (
                        <div className="gap-1 d-flex align-items-center text-secondary">
                            <span className="mb-1">{matchingEventsCount}</span>
                            <FontAwesomeIcon icon={faTag} />
                        </div>
                    )}
                </div>
            </section>

            {
                // Show analysis progress
                props.task !== undefined && (
                    <div
                        className="video-list-item-progress d-flex align-items-center justify-content-center"
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        style={{ "--progress-percentage": `${progress}%` } as any}
                    >
                        <span className="progress-label">Importing - {Math.floor(progress)}%</span>
                        <div className="inner" role="status"></div>
                    </div>
                )
            }
            {
                // Show footer when selected
                isSelected && <ReviewVideoListItemFooter data={props.data} viewId={props.viewId} />
            }
        </ListGroup.Item>
    );
}

export default ReviewVideoListItem;
